import React from 'react';
import messages from '../taskMessages';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import TaskStatusIcon from '../TaskStatusIcon';
import FormattedListValue from '@/shared/components/FormattedListValue';
import dateFormatService from '@/shared/services/DateFormatService';
import DataCacheService from '@/shared/services/DataCacheService';
import { ShareIcon } from '@/shared/components/Icons';

const translatedFieldChange = (property, originalValue, currentValue, intl) =>
  intl.formatMessage(messages.ChangedField, {
    property,
    originalValue: originalValue || '/',
    currentValue: currentValue || '/',
    b: value => <b className="dark">{value}</b>,
    i: value => <i className="dark">{value}</i>,
  });

const translatedEnumChange = (translatedEnumType, enumType, oldValue, newValue, intl) => {
  if (!oldValue) {
    return intl.formatMessage(messages.SetEnum, {
      property: translatedEnumType,
      value: formatListValue({ id: newValue, list: enumType, intl }),
      b: value => <b className="dark">{value}</b>,
      i: value => <i className="dark">{value}</i>,
    });
  }
  if (!newValue) {
    console.log('cleared', oldValue);
    return intl.formatMessage(messages.ClearEnum, {
      property: translatedEnumType,
      value: formatListValue({ id: oldValue, list: enumType, intl }),
      b: value => <b className="dark">{value}</b>,
      i: value => <i className="dark">{value}</i>,
    });
  }
  return intl.formatMessage(messages.ChangedField, {
    property: translatedEnumType,
    originalValue: formatListValue({
      id: oldValue,
      list: enumType,
      intl,
    }),
    currentValue: formatListValue({
      id: newValue,
      list: enumType,
      intl,
    }),
    b: value => <b className="dark">{value}</b>,
    i: value => <i className="dark">{value}</i>,
  });
};

const taskCreatedLog = (historyItem, taskLevel, intl) => {
  return (
    <span className="history-created">
      <FormattedMessage {...(taskLevel === 2 ? messages.ToDoCreated : messages.TaskCreated)} />
    </span>
  );
};

const formatDuration = x =>
  !x && x !== 0 ? '-' : x > 60 ? `${Math.floor(x / 60)}h ${x % 60}m` : `${x}m`;

const taskExecutionDurationChangedLog = (historyItem, intl) =>
  translatedFieldChange(
    intl.formatMessage(messages.ExecutionDuration),
    formatDuration(historyItem.oldExecutionDuration),
    formatDuration(historyItem.newExecutionDuration),
    intl,
  );

const statusChangedLog = (historyItem, intl) => (
  <span>
    <FormattedMessage
      {...messages.ChangedFieldFrom}
      values={{
        property: intl.formatMessage(messages.TaskStatus),

        b: value => <b className="dark">{value}</b>,
        i: value => <i className="dark">{value}</i>,
      }}
    />
    &nbsp;
    <span className="strike">
      <TaskStatusIcon
        status={historyItem.oldStatus}
        isBlocked={historyItem.oldStatus === 3}
        inapplicable={historyItem.oldStatus === 4}
        rejected={historyItem.oldStatus === 6}
        small
      />
      &nbsp;
      <FormattedListValue list="extendedtaskstatus" id={historyItem.oldStatus} />
    </span>
    &nbsp;
    <FormattedMessage
      {...messages.ChangedFieldTo}
      values={{
        b: value => <b className="dark">{value}</b>,
        i: value => <i className="dark">{value}</i>,
      }}
    />{' '}
    &nbsp;
    <TaskStatusIcon
      status={historyItem.newStatus}
      isBlocked={historyItem.newStatus === 3}
      inapplicable={historyItem.newStatus === 4}
      rejected={historyItem.newStatus === 6}
      small
    />
    &nbsp;
    <FormattedListValue list="extendedtaskstatus" id={historyItem.newStatus} />
  </span>
);

const parseDate = value => {
  try {
    const mom = moment.utc(value);
    if (!mom.isValid()) return null;
    return dateFormatService.formatDate(mom);
  } catch (_) {
    return null;
  }
};
const deadlineChangedLog = (historyItem, intl) =>
  translatedFieldChange(
    intl.formatMessage(messages.Deadline),
    parseDate(historyItem.oldDeadline),
    parseDate(historyItem.newDeadline),
    intl,
  );
const taskExecutionDateChangedLog = (historyItem, intl) =>
  translatedFieldChange(
    intl.formatMessage(messages.ExecutionDate),
    parseDate(historyItem.oldExecutionDate),
    parseDate(historyItem.newExecutionDate),
    intl,
  );
const assignedUserChangedLog = (historyItem, intl) =>
  translatedFieldChange(
    intl.formatMessage(messages.AssignedTo),
    historyItem.oldUserId
      ? DataCacheService.getUserName(historyItem.oldUserId)
      : intl.formatMessage(messages.Unassigned),
    historyItem.newUserId
      ? DataCacheService.getUserName(historyItem.newUserId)
      : intl.formatMessage(messages.Unassigned),
    intl,
  );
const refYearChangedLog = (historyItem, intl) =>
  translatedFieldChange(
    intl.formatMessage(messages.ReferenceYear),
    historyItem.oldRefYear,
    historyItem.newRefYear,
    intl,
  );

const taskSharedLog = (historyItem, intl) => {
  const users = historyItem.sharedWith.map(DataCacheService.getUserName);
  return (
    <span>
      <FormattedMessage {...messages.Shared} />
      &nbsp;
      <ShareIcon />
      &nbsp;
      <FormattedMessage {...messages.SharedWith} />
      &nbsp;
      <span title={users.join(', ')}>
        {users.slice(0, 5).join(', ')}
        {users.length > 5 ? ',...' : null}
      </span>
      {historyItem.sharedRemark ? (
        <i className="fa fa-comment-o fa-fw margin5L" title={historyItem.sharedRemark} />
      ) : null}
    </span>
  );
};
const TaskDetailsHistoryLog = ({ value, intl, taskLevel }) => {
  switch (value.type) {
    case 0:
      return value.oldStatus !== null
        ? statusChangedLog(value, intl)
        : taskCreatedLog(value, taskLevel, intl);
    case 1:
      return taskCreatedLog(value, taskLevel, intl);
    case 2:
      return deadlineChangedLog(value, intl);
    case 3:
      return assignedUserChangedLog(value, intl);
    case 4:
      return refYearChangedLog(value, intl);
    case 5:
      return taskSharedLog(value, intl);
    case 6:
      return taskExecutionDurationChangedLog(value, intl);
    case 7:
      return taskExecutionDateChangedLog(value, intl);
    default:
      return null;
  }
};
export default TaskDetailsHistoryLog;
